
import { useState, useEffect } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import React from 'react';

type SortDirection = 'asc' | 'desc' | 'none';

export function useTableSort<T, K extends keyof T>(
  initialData: T[], 
  defaultSortKey: K | null = null,
  defaultSortDirection: SortDirection = 'none'
) {
  const [sortConfig, setSortConfig] = useState<{
    key: K | null;
    direction: SortDirection;
  }>({
    key: defaultSortKey,
    direction: defaultSortDirection,
  });
  
  // Reset to default sort when component remounts (e.g., when returning to the page)
  useEffect(() => {
    if (defaultSortKey) {
      setSortConfig({
        key: defaultSortKey,
        direction: defaultSortDirection,
      });
    }
  }, [defaultSortKey, defaultSortDirection]);

  const sortedData = [...initialData].sort((a, b) => {
    if (!sortConfig.key || sortConfig.direction === 'none') {
      return 0;
    }

    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];

    if (aValue === bValue) {
      return 0;
    }

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Handle date comparison
    if (sortConfig.key === 'dueDate' || sortConfig.key === 'paymentDate') {
      const aDate = aValue ? new Date(aValue as string).getTime() : 0;
      const bDate = bValue ? new Date(bValue as string).getTime() : 0;
      return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
    }

    // Handle number comparison
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc'
        ? aValue - bValue
        : bValue - aValue;
    }

    // Default comparison (may not work for all types)
    return sortConfig.direction === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  const handleSort = (key: K) => {
    setSortConfig((currentConfig) => {
      if (currentConfig.key === key) {
        // Cycle through: asc -> desc -> none
        if (currentConfig.direction === 'asc') {
          return { key, direction: 'desc' };
        }
        if (currentConfig.direction === 'desc') {
          return { key: null, direction: 'none' };
        }
      }
      // First click or different key
      return { key, direction: 'asc' };
    });
  };

  const renderSortIcon = (key: K) => {
    if (sortConfig.key !== key) {
      return React.createElement(ArrowUpDown, { className: "h-4 w-4 text-gray-400" });
    }
    
    if (sortConfig.direction === 'asc') {
      return React.createElement(ArrowUp, { className: "h-4 w-4" });
    }
    
    if (sortConfig.direction === 'desc') {
      return React.createElement(ArrowDown, { className: "h-4 w-4" });
    }
    
    // If sorting is 'none', don't show any icon
    return null;
  };

  return { sortedData, sortConfig, handleSort, renderSortIcon };
}
